import { useEffect, useState } from 'react'
import { Alert, Box, InputAdornment, Switch, TextField, Typography } from '@mui/material'

/** Matches the server's MAX_CV_WAIT_HOURS (one week). */
export const MAX_CV_WAIT_HOURS = 168

export interface AutoPushCvValues {
  cvWaitHours: number
  onlyPushWithCv: boolean
}

/** "Only with CV" without a wait would mean no lead is ever pushed automatically. */
export const autoPushCvError = ({ cvWaitHours, onlyPushWithCv }: AutoPushCvValues) =>
  onlyPushWithCv && cvWaitHours === 0
    ? 'Set how many hours to wait for a CV when only leads with a CV are sent.'
    : ''

interface AutoPushCvSettingsProps {
  values: AutoPushCvValues
  onChange: (values: AutoPushCvValues) => void
  /** The ATS adds a CV to an application it already has (e.g. Onlyfy). */
  acceptsCvAfterPush?: boolean
  /** The customer's email settings ask new applicants for their CV. */
  customerAsksForCv?: boolean
  /** Days after the CV request the CV follow-up mail goes out; null when it is off. */
  cvFollowUpDays?: number | null
}

/**
 * The CV part of the auto-push settings: how long to wait for the applicant's CV before
 * sending a lead, and whether to send leads that never got one. See the server's
 * atsIntegrations/autoPush.ts.
 */
const AutoPushCvSettings = ({
  values,
  onChange,
  acceptsCvAfterPush,
  customerAsksForCv,
  cvFollowUpDays,
}: AutoPushCvSettingsProps) => {
  const error = autoPushCvError(values)
  // A CV the follow-up brings in arrives after the wait has ended.
  const followUpAfterWait =
    !acceptsCvAfterPush &&
    values.cvWaitHours > 0 &&
    cvFollowUpDays != null &&
    cvFollowUpDays * 24 >= values.cvWaitHours
  // The field's own text, so it can be empty while the user types; `values` holds the number.
  const [hoursText, setHoursText] = useState(String(values.cvWaitHours))
  useEffect(() => {
    setHoursText((text) =>
      Number(text) === values.cvWaitHours ? text : String(values.cvWaitHours)
    )
  }, [values.cvWaitHours])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pl: 1,
        borderLeft: '2px solid',
        borderColor: 'divider',
      }}
    >
      {/* Stacked rather than beside its description: the settings card is narrow, and a
          field squeezed next to a paragraph of text ends up too small to type in. */}
      <Box>
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
          Wait for CV
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p">
          Sent as soon as the CV arrives, at the latest after this time. 0 = send immediately, max.{' '}
          {MAX_CV_WAIT_HOURS} (one week).
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          value={hoursText}
          onChange={(e) => {
            // Digits only, so the field can be emptied while typing a new number.
            const digits = e.target.value.replace(/\D/g, '').slice(0, 3)
            const hours = digits === '' ? 0 : Math.min(Number(digits), MAX_CV_WAIT_HOURS)
            setHoursText(digits === '' ? '' : String(hours))
            onChange({ ...values, cvWaitHours: hours })
          }}
          onBlur={() => {
            if (hoursText === '') setHoursText('0')
          }}
          inputProps={{ inputMode: 'numeric', 'aria-label': 'Wait for CV, in hours' }}
          InputProps={{ endAdornment: <InputAdornment position="end">hours</InputAdornment> }}
          sx={{
            mt: 1,
            width: 160,
            '& .MuiOutlinedInput-root': { borderRadius: 1, bgcolor: 'background.paper' },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            Only send leads with CV
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Leads without a CV after the wait are not sent automatically. They can still be pushed
            by hand.
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={values.onlyPushWithCv}
          onChange={(e) => onChange({ ...values, onlyPushWithCv: e.target.checked })}
          color="primary"
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {acceptsCvAfterPush && values.cvWaitHours > 0 && (
        <Alert severity="info">
          This ATS receives a CV that arrives after the lead was sent, so waiting is not needed.
        </Alert>
      )}
      {!customerAsksForCv && (values.cvWaitHours > 0 || values.onlyPushWithCv) && (
        <Alert severity="warning">
          This customer does not ask new applicants for their CV (Email settings → automatic CV
          request), so few leads will get one during the wait.
        </Alert>
      )}
      {followUpAfterWait && (
        <Alert severity="info">
          The CV follow-up mail goes out {cvFollowUpDays} day{cvFollowUpDays === 1 ? '' : 's'} after
          the CV request, after this wait has ended. A CV it brings in is not sent to the ATS; it
          still reaches the customer by email.
        </Alert>
      )}
    </Box>
  )
}

export default AutoPushCvSettings
